import type { BifrostWeb, CallOptions } from '@seekrtech/bifrost-web'
import {
  MAX_NATIVE_READ_ATTEMPT_MS,
  beginNativeReadAttempt,
  markNeverReplay,
  nativeReadRetryInstruction,
} from '../page-loading-recovery-internal'
import type { Result } from '../types'

/**
 * The status read is not any page's reveal gate — the walkthrough reveals on
 * its own animation — so revealing must not end its recovery. It stays bounded
 * by the presentation's original deadline (spec Decision 10).
 */
const STATUS_READ_SCOPE = { survivesReveal: true }

export { STATUS_READ_SCOPE as notificationPermissionStatusScope }

export interface PermissionServiceMethods {
  getHasNotificationPermission: () => Promise<Result<boolean>>
  requestNotificationPermission: () => Promise<Result<boolean>>
}

interface PermissionServiceProxyMethods {
  getHasNotificationPermission: (
    options?: CallOptions,
  ) => Promise<Result<boolean>>
  requestNotificationPermission: () => Promise<Result<boolean>>
}

export function createPermissionService(
  bifrost: BifrostWeb,
): PermissionServiceMethods {
  const proxy = bifrost
    .createProxyBuilder<PermissionServiceProxyMethods>('PermissionService')
    .withDefaultOptions({ timeout: 31_536_000_000 })
    .build()

  return {
    // A notification-permission status read is a verified repeatable read: it
    // shows no UI and returns the current status. Inside a native presentation
    // it is bounded by that presentation's page-loading budget so a lost reply
    // can be read again before the original deadline.
    getHasNotificationPermission: async () => {
      const attempt = beginNativeReadAttempt(undefined, STATUS_READ_SCOPE)
      if (!attempt) {
        // No presentation armed a budget — the page-visible announcement never
        // arrived, or there is no native cover at all. This proxy's default
        // timeout is a year, so a dropped reply on that path never rejects and
        // the caller awaits a promise that never settles; the walkthrough's
        // stage 6 awaits exactly this with interaction already disabled. Give
        // the read the same single-attempt ceiling recovery uses, so it reaches
        // the caller's existing not-granted continuation instead of hanging.
        // There is no budget to retry inside, so this is one attempt.
        return proxy.getHasNotificationPermission({
          timeout: MAX_NATIVE_READ_ATTEMPT_MS,
        })
      }

      try {
        // A delivered status is the answer, even if reveal, the deadline or
        // leaving the page landed while it was in flight; only a failure needs
        // to ask whether another attempt is still allowed.
        return await proxy.getHasNotificationPermission({
          timeout: attempt.timeout,
        })
      } catch (error) {
        const instruction = nativeReadRetryInstruction(
          attempt,
          error,
          STATUS_READ_SCOPE,
        )
        throw instruction ?? error
      }
    },
    // Requesting the permission shows the system prompt. An admitted request
    // may already have prompted the user, so it keeps its own unbounded call
    // and its failures are marked never-replay.
    requestNotificationPermission: async () => {
      try {
        return await proxy.requestNotificationPermission()
      } catch (error) {
        throw markNeverReplay(error)
      }
    },
  }
}
